import { useCopy } from './useCopy.js'

/**
 * A value somebody is about to paste somewhere else.
 *
 * A DNS record value is long, has no spaces to break on, and goes into a form
 * on another website. Selecting it by hand is where a missing final character
 * comes from, and a record that is one character short fails in a way nothing
 * explains — so there is a button.
 *
 * The "copied" flash is local UI state and lives here rather than in a view
 * model. It is not about the product: nothing outside this component can
 * observe it, and it exists for about a second.
 *
 * The clipboard itself is `useCopy`, shared with Compose's code panel. The
 * value stays selectable text whatever happens, so a refusal leaves somebody
 * something to do.
 */
export function CopyableValue({ value }: { readonly value: string }) {
  const { state, copy } = useCopy()

  return (
    <span className="flex items-start gap-2">
      <code className="min-w-0 flex-1 overflow-x-auto rounded-field border border-line bg-surface-2 px-2 py-1.5 font-mono text-[12px] text-t1">
        {value}
      </code>
      <button
        type="button"
        onClick={() => copy(value)}
        className="flex-none rounded-field border border-line bg-surface px-2.5 py-1.5 font-medium text-[12px] text-t2"
      >
        {LABELS[state]}
      </button>
    </span>
  )
}

const LABELS = {
  idle: 'Copy',
  copied: 'Copied',
  // Never silently nothing: a button that did not work has to say so, or
  // somebody pastes whatever was on the clipboard before.
  refused: 'Select it',
} as const
